import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { lazy, Suspense, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Clock, CalendarDays, Table2, LayoutDashboard, BarChart3, FolderKanban,
  Building2, Tag as TagIcon, Users, Settings as SettingsIcon, Sun, Moon,
  ChevronsUpDown, LogOut, Check, CalendarCheck, Menu as MenuIcon, X,
} from "lucide-react";
import { api } from "@/api/client";
import { invalidateWorkspace } from "@/features/data";
import { useWorkspaceId } from "@/app/session";
import { queryClient } from "@/app/query";
import { Button, IconButton } from "@/components/ui";
import { useToast, errorMessage } from "@/components/Toast";
import { fromISODate } from "@/lib/dates";
import { cn } from "@/lib/cn";
import { Link, useRouter } from "@/app/router";
import { useSession } from "@/app/session";
import { getTheme, toggleTheme, type Theme } from "@/app/theme";
import { TimerBar } from "@/features/tracker/TimerBar";
import { EntryDialog, newEntryState, type EntryDialogState } from "@/components/EntryDialog";
import { ShortcutsDialog } from "@/components/ShortcutsDialog";
import { shortcutAllowed } from "@/lib/keys";
import { TrackerPage } from "@/features/tracker/TrackerPage";
import { ProjectsPage } from "@/features/projects/ProjectsPage";
import { ClientsPage } from "@/features/clients/ClientsPage";
import { TagsPage } from "@/features/tags/TagsPage";
import { TeamPage } from "@/features/team/TeamPage";
import { SettingsPage } from "@/features/settings/SettingsPage";
import { TimesheetPage } from "@/features/timesheet/TimesheetPage";
import { CalendarPage } from "@/features/calendar/CalendarPage";
import { Spinner } from "@/components/ui";

// Dashboard and Reports carry the charting library (~400 kB min) — load them
// on demand so the main bundle stays lean for the daily tracker/calendar use.
const DashboardPage = lazy(() => import("@/features/dashboard/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const ReportsPage = lazy(() => import("@/features/reports/ReportsPage").then((m) => ({ default: m.ReportsPage })));

const NAV = [
  { to: "/tracker", label: "Tracker", icon: Clock },
  { to: "/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/timesheet", label: "Timesheet", icon: Table2 },
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/projects", label: "Projects", icon: FolderKanban },
  { to: "/clients", label: "Clients", icon: Building2 },
  { to: "/tags", label: "Tags", icon: TagIcon },
  { to: "/team", label: "Team", icon: Users },
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

export function AppShell() {
  const { path } = useRouter();
  const [navOpen, setNavOpen] = useState(false);

  // Close the drawer whenever navigation happens.
  useEffect(() => setNavOpen(false), [path]);

  return (
    <div className="flex h-full">
      <div className="hidden lg:block">
        <Sidebar path={path} />
      </div>
      {navOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setNavOpen(false)} aria-hidden />
          <div className="absolute inset-y-0 left-0 shadow-2xl">
            <Sidebar path={path} />
          </div>
        </div>
      )}
      <div className="flex min-w-0 flex-1 flex-col">
        <TopBar onMenu={() => setNavOpen(true)} />
        <TimerBar />
        <PendingCloseBanner />
        <main className="flex-1 overflow-auto">
          <Suspense fallback={<div className="flex justify-center py-16"><Spinner /></div>}>
            <CurrentPage path={path} />
          </Suspense>
        </main>
      </div>
      <GlobalShortcuts />
    </div>
  );
}

const DISMISS_KEY = "timer.closedismissed";

/** PendingCloseBanner reminds about months awaiting close (DESIGN.md B9). */
function PendingCloseBanner() {
  const wid = useWorkspaceId();
  const { toast } = useToast();
  const [dismissed, setDismissed] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(DISMISS_KEY) ?? "[]") as string[];
    } catch {
      return [];
    }
  });
  const { data: months } = useQuery({
    queryKey: ["periods-pending", wid],
    queryFn: () => api.get<{ months: string[] }>(`/workspaces/${wid}/periods/pending`).then((r) => r.months ?? []),
    enabled: !!wid,
    staleTime: 60 * 60_000,
  });

  const month = (months ?? []).filter((m) => !dismissed.includes(m)).sort()[0];
  if (!month) return null;
  const label = fromISODate(`${month}-01`).toLocaleDateString(undefined, { month: "long", year: "numeric" });

  async function closeNow() {
    try {
      await api.post(`/workspaces/${wid}/periods/close`, { month, scope: "user" });
      invalidateWorkspace(wid);
      void queryClient.invalidateQueries({ queryKey: ["periods-pending", wid] });
      toast(`${label} closed`, "success");
    } catch (err) {
      toast(errorMessage(err), "error");
    }
  }

  function dismiss() {
    const next = [...dismissed, month];
    setDismissed(next);
    localStorage.setItem(DISMISS_KEY, JSON.stringify(next));
  }

  return (
    <div className="flex items-center gap-3 border-b border-line bg-warn/10 px-4 py-2 text-sm">
      <CalendarCheck className="size-4 shrink-0 text-warn" aria-hidden />
      <span className="min-w-0 flex-1 truncate text-ink">
        Close your {label} — review your entries and lock the month for reporting.
      </span>
      <Button size="sm" variant="secondary" onClick={closeNow}>Close {label}</Button>
      <IconButton label="Dismiss reminder" onClick={dismiss}><X className="size-4" /></IconButton>
    </div>
  );
}

/** GlobalShortcuts wires `n` (new entry) and `?` (help) app-wide. */
function GlobalShortcuts() {
  const [entry, setEntry] = useState<EntryDialogState | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!shortcutAllowed(e)) return;
      if (e.key === "n") {
        e.preventDefault();
        setEntry(newEntryState());
      } else if (e.key === "?") {
        e.preventDefault();
        setHelpOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      {entry && <EntryDialog state={entry} onClose={() => setEntry(null)} />}
      <ShortcutsDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
}

function Sidebar({ path }: { path: string }) {
  return (
    <aside className="flex h-full w-56 shrink-0 flex-col border-r border-line bg-surface">
      <div className="flex h-14 items-center gap-2.5 px-5">
        <span className="size-3 rounded-full bg-running" />
        <span className="text-[15px] font-semibold tracking-tight">Timer</span>
      </div>
      <nav className="flex-1 space-y-0.5 px-3 py-2">
        {NAV.map((item) => {
          const active = path.startsWith(item.to) || (item.to === "/tracker" && path === "/");
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              className={cn(
                "flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors",
                active ? "bg-elevated font-medium text-ink" : "text-muted hover:text-ink hover:bg-elevated/60",
              )}
            >
              <Icon className={cn("size-4", active ? "text-accent" : "text-faint")} />
              {item.label}
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}

function TopBar({ onMenu }: { onMenu: () => void }) {
  const { workspace, workspaces, selectWorkspace, me, logout } = useSession();
  const { navigate } = useRouter();
  const [theme, setTheme] = useState<Theme>(getTheme());

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-surface px-4">
      <button
        onClick={onMenu}
        aria-label="Open navigation"
        className="inline-flex size-8 items-center justify-center rounded-md text-muted hover:bg-elevated hover:text-ink lg:hidden"
      >
        <MenuIcon className="size-4" />
      </button>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm font-medium hover:bg-elevated">
            {workspace?.workspace_name ?? "Workspace"}
            <ChevronsUpDown className="size-3.5 text-faint" />
          </button>
        </DropdownMenu.Trigger>
        <Menu>
          {workspaces.map((w) => (
            <MenuItem key={w.workspace_id} onSelect={() => selectWorkspace(w.workspace_id)}>
              <span className="flex-1 truncate">{w.workspace_name}</span>
              <span className="text-xs text-faint">{w.role}</span>
              {workspace?.workspace_id === w.workspace_id && <Check className="size-4 text-accent" />}
            </MenuItem>
          ))}
        </Menu>
      </DropdownMenu.Root>

      <div className="flex-1" />

      <button
        onClick={() => setTheme(toggleTheme())}
        className="inline-flex size-8 items-center justify-center rounded-md text-muted hover:bg-elevated hover:text-ink"
        aria-label="Toggle theme"
      >
        {theme === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
      </button>

      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button className="flex size-8 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-ink">
            {me?.user.name?.[0]?.toUpperCase() ?? "?"}
          </button>
        </DropdownMenu.Trigger>
        <Menu align="end">
          <div className="px-2.5 py-1.5">
            <p className="truncate text-sm font-medium">{me?.user.name}</p>
            <p className="truncate text-xs text-faint">{me?.user.email}</p>
          </div>
          <DropdownMenu.Separator className="my-1 h-px bg-line" />
          <MenuItem onSelect={() => navigate("/settings")}>Settings</MenuItem>
          <MenuItem
            onSelect={async () => {
              await logout();
              navigate("/login", { replace: true });
            }}
          >
            <LogOut className="size-4" /> Sign out
          </MenuItem>
        </Menu>
      </DropdownMenu.Root>
    </header>
  );
}

function Menu({ children, align = "start" }: { children: React.ReactNode; align?: "start" | "end" }) {
  return (
    <DropdownMenu.Portal>
      <DropdownMenu.Content
        align={align}
        sideOffset={6}
        className="z-50 min-w-52 rounded-lg border border-line bg-elevated p-1 shadow-xl"
      >
        {children}
      </DropdownMenu.Content>
    </DropdownMenu.Portal>
  );
}

function MenuItem({ children, onSelect }: { children: React.ReactNode; onSelect: () => void }) {
  return (
    <DropdownMenu.Item
      onSelect={onSelect}
      className="flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-ink outline-none data-[highlighted]:bg-surface"
    >
      {children}
    </DropdownMenu.Item>
  );
}

function CurrentPage({ path }: { path: string }) {
  if (path.startsWith("/dashboard")) return <DashboardPage />;
  if (path.startsWith("/timesheet")) return <TimesheetPage />;
  if (path.startsWith("/calendar")) return <CalendarPage />;
  if (path.startsWith("/reports")) return <ReportsPage />;
  if (path.startsWith("/projects")) return <ProjectsPage />;
  if (path.startsWith("/clients")) return <ClientsPage />;
  if (path.startsWith("/tags")) return <TagsPage />;
  if (path.startsWith("/team")) return <TeamPage />;
  if (path.startsWith("/settings")) return <SettingsPage />;
  return <TrackerPage />;
}
